import { Container } from '@ag1/ui';
import { useHeaderContext } from '@portal/state/context/HeaderContext';
import type { InferGetStaticPropsType } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LocaleEnum, languages } from 'schema/locale';
import type { PageTranslation } from 'schema/studio/studio';
import PageHeaderFooterLayout from 'shared/layouts/PageHeaderFooterLayout';
import SubscribeSection from 'shared/studio/SubscribeSection/SubscribeSection';
import { urlFor } from 'shared/utils/urlFor';
import { routes } from '@portal/constants/routes';
import { HeaderBackground } from 'types/studio/header';
import { EduArticleCards } from './components/EduArticleCards';
import { EduCategoryHeader } from './components/EduCategoryHeader';
import { EduFeaturedArticle } from './components/EduFeaturedArticle';
import { LoadMoreArticlesButton } from './components/LoadMoreArticlesButton';
import { ARTICLES_BY_PAGE, pageTranslationsLinks } from './constants';
import styles from './EduHubPage.module.scss';
import type { getStaticProps } from './getStaticProps';
import { loadArticlesCount, loadEduHubArticles } from './loadEduHubArticles';

export const EduHubPage = ({
  pageData,
  footerData,
  articles: initialArticles,
  headerData: initialHeaderData,
  category,
  featuredArticle,
  locale
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const router = useRouter();
  const { slug = '' } = router.query;
  const { eduHub, eduHubCategory } = routes;

  const { getHeaderDataDefaults, useSetHeaderOptions } = useHeaderContext();
  useSetHeaderOptions(initialHeaderData);
  const headerData = getHeaderDataDefaults(initialHeaderData);

  const {
    title,
    subtitle,
    seo,
    loadMoreButton,
    categories,
    subscribeSection,
    showSubscribeSection,
    structuredSchemaData,
    canonical
  } = pageData;

  const [articles, setArticles] = useState(initialArticles);
  const [totalArticles, setTotalArticles] = useState(initialArticles.length);

  useEffect(() => {
    (async () => {
      const featuredArticleSlug = featuredArticle?.slug?.current || '';

      const articlesCount = await loadArticlesCount({
        categorySlug: slug.toString() || '',
        featuredArticleSlug: featuredArticleSlug || '',
        locale
      });
      setTotalArticles(articlesCount);
    })();
  }, [slug, locale, featuredArticle]);

  const handleLoadMore = useCallback(async () => {
    const newArticles = await loadEduHubArticles({
      categorySlug: slug.toString() || '',
      startWith: articles.length,
      endWith: articles.length + ARTICLES_BY_PAGE,
      featuredArticle,
      locale
    });
    setArticles((prevArticles) => [...prevArticles, ...newArticles]);
  }, [slug, articles.length, locale, featuredArticle]);

  const showMoreButton = totalArticles > articles.length;

  const pageTranslations = useMemo((): PageTranslation[] => {
    if (!slug || !category) {
      return pageTranslationsLinks;
    }

    const getCategorySlug = (language: LocaleEnum): string | null => {
      const currentCategory = category?.translations?.find((item) => item?.language === language);

      return currentCategory?.slug?.current || null;
    };

    return languages.map((language) => ({
      slug: {
        _type: 'slug',
        current: getCategorySlug(language)
          ? `${eduHubCategory({ slug: getCategorySlug(language) || '' })}`
          : eduHub
      },
      language
    }));
  }, [category, slug, eduHub, eduHubCategory]);

  return (
    <PageHeaderFooterLayout
      footerData={footerData}
      headerData={headerData}
      title={seo?.title || 'AG1 by Athletic Greens® - Official Site'}
      coverImage={seo?.image && urlFor(seo?.image)?.url}
      headerBackground={HeaderBackground.white}
      pageTranslations={pageTranslations}
      structuredSchemaData={structuredSchemaData}
      canonicalLinkData={canonical}>
      <Container narrow>
        <EduCategoryHeader
          title={title}
          subtitle={subtitle}
          categories={categories || []}
          titleForAllArticles={pageData.titleForAllArticles}
          activeCategory={category || undefined}
        />
        <section className={styles.articles}>
          <div className={styles.grid}>
            {featuredArticle && <EduFeaturedArticle featuredArticle={featuredArticle} />}
            <EduArticleCards articles={articles} hasFeaturedArticle={!!featuredArticle} />
          </div>

          {showMoreButton && (
            <LoadMoreArticlesButton loadMoreButton={loadMoreButton} onClick={handleLoadMore} />
          )}
        </section>
      </Container>

      {!!showSubscribeSection && !!subscribeSection && (
        <SubscribeSection
          _type="subscribeSection"
          headline={subscribeSection.headline}
          subtitle={subscribeSection.subtitle}
          placeholder={subscribeSection.placeholder}
          buttonLabel={subscribeSection.buttonLabel}
          successButtonLabel={subscribeSection.successButtonLabel}
          errorMessage={subscribeSection.errorMessage}
          theme={subscribeSection.theme}
          visibleOnPageLoad={false}
          _key="subscribeSection"
        />
      )}
    </PageHeaderFooterLayout>
  );
};
